import XDate from 'xdate'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import {
  faBuildingColumns,
  faCreditCard,
  faFileShield,
  faIdCard,
  faPiggyBank,
} from '@fortawesome/pro-duotone-svg-icons'

import {
  QuickPassPublicSharePagePayload,
  QuickPassPublicVerification,
} from '../../_types/quickpass-share-page'
import { VerificationProof } from './types'

const formatCurrency = (value: number): string =>
  `$${value.toLocaleString('en-CA')}`

const formatBudget = (
  preferences: QuickPassPublicSharePagePayload['preferences'],
): string => {
  if (preferences === null) return ''
  if (preferences.priceMin === null && preferences.priceMax === null) {
    return 'Open to a range'
  }
  if (preferences.priceMin !== null && preferences.priceMax !== null) {
    return `${formatCurrency(preferences.priceMin)} – ${formatCurrency(preferences.priceMax)} / month`
  }
  if (preferences.priceMin !== null) {
    return `From ${formatCurrency(preferences.priceMin)} / month`
  }
  if (preferences.priceMax !== null) {
    return `Up to ${formatCurrency(preferences.priceMax)} / month`
  }
  return ''
}

const formatBedrooms = (counts: number[]): string => {
  if (counts.length === 0) return 'Open to any size'
  return counts
    .map(count => (count === 0 ? 'Studio' : `${count.toString()} bed`))
    .join(' · ')
}

const formatBathrooms = (counts: number[]): string => {
  if (counts.length === 0) return 'Any'
  return counts.map(count => `${count.toString()} bath`).join(' · ')
}

const DWELLING_SCOPE_LABELS: Record<
  'whole-property' | 'unit' | 'no-preference',
  string
> = {
  'whole-property': 'Whole place',
  unit: 'A unit',
  'no-preference': 'Either',
}

const PROPERTY_TYPE_LABELS: Record<string, string> = {
  apartment: 'Apartment',
  condo: 'Condo',
  house: 'House',
  townhouse: 'Townhouse',
  custom: 'Custom',
}

const formatDwellingScope = (
  scope: 'whole-property' | 'unit' | 'no-preference',
): string => {
  return DWELLING_SCOPE_LABELS[scope] ?? 'Any'
}

const formatPropertyTypes = (types: string[]): string => {
  if (types.length === 0) return 'Open to any place'
  return types.map(type => PROPERTY_TYPE_LABELS[type] ?? type).join(' · ')
}

const formatMoveInDate = (timestamp: number | null): string => {
  if (timestamp === null || timestamp <= 0) return ''
  return new XDate(timestamp).toString('MMMM d, yyyy')
}

const formatMemberSince = (timestamp: number): string => {
  if (timestamp <= 0) return ''
  return new XDate(timestamp).toString('MMMM yyyy')
}

const AUTH_PROVIDER_LABELS: Record<string, string> = {
  'email-password': 'Email verified',
  google: 'Google verified',
  'magic-link': 'Magic-link verified',
}

const formatAuthProvider = (provider: string): string => {
  return AUTH_PROVIDER_LABELS[provider] ?? provider
}

const formatIncome = (income: number | null): string => {
  if (income === null || income <= 0) return ''
  return `${formatCurrency(income)} / month after tax`
}

const buildInitials = (displayName: string): string => {
  const parts = displayName.trim().split(/\s+/u)
  if (parts.length === 0) return 'Q'
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
  return (
    parts[0].charAt(0).toUpperCase() +
    parts[parts.length - 1].charAt(0).toUpperCase()
  )
}

interface VerificationTypeConfig {
  icon: IconDefinition
  title: string
  badgeLabel: string
  method: string
  buildProves: (name: string) => string
}

interface VerificationProviderConfig {
  name: string
  blurb: string
}

/**
 * Static presentation copy per verification type (icon, labels, method, the
 * "what this proves" sentence). The qualitative RESULT shown to the landlord
 * comes from the server (`verification.displayValue`) — the server emits a
 * privacy-safe label and never sends the exact income/balance — so it isn't
 * defined here.
 */
const VERIFICATION_TYPE_CONFIG: Record<string, VerificationTypeConfig> = {
  identity: {
    icon: faIdCard,
    title: 'Identity',
    badgeLabel: 'ID verified',
    method: 'Photo ID + live selfie match',
    buildProves: name =>
      `A government-issued photo ID was checked against a live selfie, confirming ${name} is a real person using their real, legal name — not a stolen or made-up identity.`,
  },
  income: {
    icon: faBuildingColumns,
    title: 'Income',
    badgeLabel: 'Income verified',
    method: 'Read-only payroll & bank connection',
    buildProves: name =>
      `${name} connected their income source directly, so their earnings are verified straight from the bank or payroll — confirming real, recurring income, not a figure typed into a form. The exact amount stays private.`,
  },
  funds: {
    icon: faPiggyBank,
    title: 'Funds',
    badgeLabel: 'Funds verified',
    method: 'Read-only bank balance check',
    buildProves: name =>
      `${name}'s account balances were checked in real time, confirming they have genuine savings on hand for move-in — without revealing the exact balance.`,
  },
  credit: {
    icon: faCreditCard,
    title: 'Credit',
    badgeLabel: 'Credit checked',
    method: 'Soft check — no score impact',
    buildProves: name =>
      `A soft credit check was completed — enough to confirm creditworthiness, with no impact to ${name}'s credit score.`,
  },
  background: {
    icon: faFileShield,
    title: 'Background',
    badgeLabel: 'Background checked',
    method: 'Accredited records search',
    buildProves: name =>
      `An accredited background and eviction-history search on ${name} was completed by a licensed screening provider.`,
  },
}

/**
 * Who-they-are blurbs keyed by the real provider the server reports, so a
 * landlord who's never heard of the partner gets recognisable context.
 */
const VERIFICATION_PROVIDER_CONFIG: Record<string, VerificationProviderConfig> =
  {
    didit: {
      name: 'Didit',
      blurb:
        'Didit is an identity verification company. It reads a government photo ID, checks it for tampering, and matches it against a live selfie to confirm a real person is present.',
    },
    plaid: {
      name: 'Plaid',
      blurb:
        'Plaid is the secure bank connection behind apps like Venmo, Wealthsimple and SoFi. It reads verified financial data read-only — it never moves money or sees login passwords.',
    },
    certn: {
      name: 'Certn',
      blurb:
        'Certn is an accredited screening company that runs background and credit checks for employers and landlords across North America.',
    },
    singlekey: {
      name: 'SingleKey',
      blurb:
        'SingleKey is a tenant-screening service used by landlords across North America for credit and background checks.',
    },
  }

const FALLBACK_PROVIDER: VerificationProviderConfig = {
  name: 'a trusted partner',
  blurb: '',
}

/**
 * Resolves a public verification into a presentation-ready proof, or null for
 * types that aren't meaningful trust signals to a landlord (e.g. email/phone).
 * The result + provider come from the server (already privacy-safe); this only
 * layers on the type's presentation copy and the provider's "who they are".
 *
 * @param {QuickPassPublicVerification} verification - The public verification.
 * @param {string} firstName - The renter's first name for natural copy.
 * @returns {VerificationProof | null} The resolved proof, or null to skip.
 */
const getVerificationProof = (
  verification: QuickPassPublicVerification,
  firstName: string,
): VerificationProof | null => {
  const typeConfig = VERIFICATION_TYPE_CONFIG[verification.type]
  if (typeConfig === undefined) {
    return null
  }
  const providerConfig =
    VERIFICATION_PROVIDER_CONFIG[verification.provider] ?? FALLBACK_PROVIDER
  const name = firstName.trim() !== '' ? firstName.trim() : 'This renter'
  return {
    icon: typeConfig.icon,
    title: typeConfig.title,
    badgeLabel: typeConfig.badgeLabel,
    result: verification.displayValue,
    proves: typeConfig.buildProves(name),
    providerName: providerConfig.name,
    providerBlurb: providerConfig.blurb,
    method: typeConfig.method,
    verifiedAt: verification.verifiedAt,
  }
}

/**
 * Formats a verification timestamp as a short, human date (e.g. "Jun 2, 2026").
 *
 * @param {number | null} timestamp - The verifiedAt timestamp.
 * @returns {string} The formatted date, or '' when unset.
 */
const formatVerifiedDate = (timestamp: number | null): string => {
  if (timestamp === null || timestamp <= 0) {
    return ''
  }
  return new XDate(timestamp).toString('MMM d, yyyy')
}

const VERIFICATIONS_CONTACT_EMAIL = 'verifications@quickpass.club'

/**
 * Builds a pre-filled mailto link a landlord can use to ask the QuickPass team
 * to independently confirm a renter's pass. Pre-fills the renter and pass URL
 * so the team knows exactly which verification is being queried.
 *
 * @param {string} displayName - The renter's display name.
 * @param {string} slug - The pass slug (used to rebuild the pass URL).
 * @returns {string} A mailto: href with encoded subject + body.
 */
const buildVerificationContactHref = (
  displayName: string,
  slug: string,
): string => {
  const subject = `Confirming a QuickPass — ${displayName}`
  const body = [
    'Hi QuickPass team,',
    '',
    "I'd like independent confirmation of the verifications on this renter's QuickPass:",
    '',
    `Renter: ${displayName}`,
    `Pass: https://app.quickpass.club/#/me/${slug}`,
    '',
    'My question:',
    '',
  ].join('\n')
  return `mailto:${VERIFICATIONS_CONTACT_EMAIL}?subject=${encodeURIComponent(
    subject,
  )}&body=${encodeURIComponent(body)}`
}

/**
 * Derives a stable, credential-style pass number from the slug + member-since
 * year (e.g. "QP·2025·0042"). Deterministic — the same pass always
 * shows the same number. Purely cosmetic, not a real identifier.
 *
 * @param {string} slug - The pass slug.
 * @param {number} memberSinceTimestamp - The renter's member-since timestamp.
 * @returns {string} The formatted pass number.
 */
const buildPassNumber = (
  slug: string,
  memberSinceTimestamp: number,
): string => {
  let hash = 0
  for (let index = 0; index < slug.length; index += 1) {
    hash = (hash * 31 + slug.charCodeAt(index)) % 10000
  }
  const code = hash.toString().padStart(4, '0')
  const year =
    memberSinceTimestamp > 0
      ? new XDate(memberSinceTimestamp).toString('yyyy')
      : new XDate().toString('yyyy')
  return `QP·${year}·${code}`
}

export {
  formatBudget,
  formatBedrooms,
  formatBathrooms,
  formatDwellingScope,
  formatPropertyTypes,
  formatMoveInDate,
  formatMemberSince,
  formatAuthProvider,
  formatIncome,
  buildInitials,
  getVerificationProof,
  formatVerifiedDate,
  VERIFICATIONS_CONTACT_EMAIL,
  buildVerificationContactHref,
  buildPassNumber,
}
